import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import {
  fragmentMoney,
  fragmentVariant,
  productFragment,
  productFragmentDetails,
  productInsFrament,
  productVariantAttributesFragment
} from "@saleor/fragments/products";
import { taxTypeFragment } from "@saleor/fragments/taxes";
import { warehouseFragment } from "@saleor/fragments/warehouses";
import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import { BulkOperationProductsEvent } from "./types/BulkOperationProductsEvent";
import { CountAllProducts } from "./types/CountAllProducts";
import {
  CreateMultipleVariantsData,
  CreateMultipleVariantsDataVariables
} from "./types/CreateMultipleVariantsData";
import {
  ExtraChargeCategoryData,
  ExtraChargeCategoryDataVariables
} from "./types/ExtraChargeCategoryData";
import {
  GetErpProductVariant,
  GetErpProductVariantVariables
} from "./types/GetErpProductVariant";
import {
  GridAttributes,
  GridAttributesVariables
} from "./types/GridAttributes";
import {
  InitialProductFilterData,
  InitialProductFilterDataVariables
} from "./types/InitialProductFilterData";
// import {
//   InstagramsByProductsId,
//   InstagramsByProductsIdVariables
// } from "./types/InstagramsByProductsId";
import {
  ProductDetails,
  ProductDetailsVariables
} from "./types/ProductDetails";
import {
  ProductImageById,
  ProductImageByIdVariables
} from "./types/ProductImageById";
import { ProductList, ProductListVariables } from "./types/ProductList";
import {
  ProductVariantCreateData,
  ProductVariantCreateDataVariables
} from "./types/ProductVariantCreateData";
import {
  ProductVariantDetails,
  ProductVariantDetailsVariables
} from "./types/ProductVariantDetails";
import {
  SkuExtraChargeCategory,
  SkuExtraChargeCategoryVariables
} from "./types/SkuExtraChargeCategory";
import {
  VariantImageById,
  VariantImageByIdVariables
} from "./types/VariantImageById";

const initialProductFilterDataQuery = gql`
  query InitialProductFilterData(
    $categories: [ID!]
    $collections: [ID!]
    $productTypes: [ID!]
  ) {
    attributes(first: 100, filter: { filterableInDashboard: true }) {
      totalCount
      edges {
        node {
          id
          name
          slug
          values {
            id
            name
            slug
          }
        }
      }
    }
    categories(first: 100, filter: { ids: $categories }) {
      edges {
        node {
          id
          name
        }
      }
    }
    collections(first: 100, filter: { ids: $collections }) {
      edges {
        node {
          id
          name
        }
      }
    }
    productTypes(first: 100, filter: { ids: $productTypes }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const useInitialProductFilterDataQuery = makeQuery<
  InitialProductFilterData,
  InitialProductFilterDataVariables
>(initialProductFilterDataQuery);

const productListQuery = gql`
  ${fragmentMoney}
  ${productFragment}
  query ProductList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: ProductFilterInput
    $sort: ProductOrder
  ) {
    products(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      sortBy: $sort
    ) {
      totalCount
      edges {
        node {
          ...ProductFragment
          attributes {
            attribute {
              id
            }
            values {
              id
              name
            }
          }
          pricing {
            priceRangeUndiscounted {
              start {
                gross {
                  ...Money
                }
              }
              stop {
                gross {
                  ...Money
                }
              }
            }
          }
        }
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;
export const useProductListQuery = makeQuery<ProductList, ProductListVariables>(
  productListQuery
);

const countAllProductsQuery = gql`
  query CountAllProducts {
    products {
      totalCount
    }
  }
`;
export const useCountAllProducts = makeQuery<CountAllProducts, null>(
  countAllProductsQuery
);

const productDetailsQuery = gql`
  ${productInsFrament}
  ${productFragmentDetails}
  ${taxTypeFragment}
  query ProductDetails($id: ID!, $isOrder: String) {
    product(id: $id) {
      ...Product
      productIns(isOrder: $isOrder) {
        ...ProductInsFrament
      }
      productSortIns {
        ...ProductInsFrament
      }
    }
    taxTypes {
      ...TaxTypeFragment
    }
  }
`;
export const useProductDetails = makeQuery<
  ProductDetails,
  ProductDetailsVariables
>(productDetailsQuery);

const productVariantQuery = gql`
  ${fragmentVariant}
  query ProductVariantDetails($id: ID, $sku: String) {
    productVariant(id: $id, sku: $sku) {
      ...ProductVariant
    }
  }
`;
export const useProductVariantQuery = makeQuery<
  ProductVariantDetails,
  ProductVariantDetailsVariables
>(productVariantQuery);

const productVariantCreateQuery = gql`
  query ProductVariantCreateData($id: ID!) {
    product(id: $id) {
      id
      images {
        id
        sortOrder
        url
      }
      name
      productType {
        id
        variantAttributes {
          id
          slug
          name
          valueRequired
          values {
            id
            name
            slug
          }
        }
      }
      thumbnail {
        url
      }
      variants {
        id
        name
        sku
        variantId
        images {
          id
          url
          isFirst
        }
        thumbnail {
          url
        }
        isPublished
      }
    }
  }
`;
export const useProductVariantCreateQuery = makeQuery<
  ProductVariantCreateData,
  ProductVariantCreateDataVariables
>(productVariantCreateQuery);

const productImageQuery = gql`
  query ProductImageById($productId: ID!, $imageId: ID!) {
    product(id: $productId) {
      id
      name
      mainImage: imageById(id: $imageId) {
        id
        alt
        url
      }
      images {
        id
        url
      }
    }
  }
`;
export const useProductImageQuery = makeQuery<
  ProductImageById,
  ProductImageByIdVariables
>(productImageQuery);

const variantImageQuery = gql`
  query VariantImageById($variantId: ID!, $imageId: ID!) {
    productVariant(id: $variantId) {
      id
      name
      skuName
      mainImage: imageById(id: $imageId) {
        id
        alt
        url
      }
      images {
        id
        url
        urlType
      }
    }
  }
`;
export const useVariantImageQuery = makeQuery<
  VariantImageById,
  VariantImageByIdVariables
>(variantImageQuery);

const availableInGridAttributes = gql`
  ${pageInfoFragment}
  query GridAttributes($first: Int!, $after: String, $ids: [ID!]!) {
    availableInGrid: attributes(
      first: $first
      after: $after
      filter: { availableInGrid: true, isVariantOnly: false }
    ) {
      edges {
        node {
          id
          name
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
      totalCount
    }

    grid: attributes(first: 25, filter: { ids: $ids }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export const useAvailableInGridAttributesQuery = makeQuery<
  GridAttributes,
  GridAttributesVariables
>(availableInGridAttributes);

const createMultipleVariantsData = gql`
  ${productVariantAttributesFragment}
  ${warehouseFragment}
  query CreateMultipleVariantsData($id: ID!) {
    product(id: $id) {
      ...ProductVariantAttributesFragment
    }
    warehouses(first: 20) {
      edges {
        node {
          ...WarehouseFragment
        }
      }
    }
  }
`;
export const useCreateMultipleVariantsData = makeQuery<
  CreateMultipleVariantsData,
  CreateMultipleVariantsDataVariables
>(createMultipleVariantsData);

const extraChargeCategoryData = gql`
  query ExtraChargeCategoryData($sku: String) {
    extraChargeCategory(sku: $sku)
  }
`;
export const useExtraChargeCategoryDataQuery = makeQuery<
  ExtraChargeCategoryData,
  ExtraChargeCategoryDataVariables
>(extraChargeCategoryData);

// const instagramsByProductsId = gql`
//   ${pageInfoFragment}
//   query InstagramsByProductsId($after: String, $first: Int!,$id:ID!,$query:String!,$isOrder:Boolean){
//     instagramsByProductsId(after: $after, first: $first,id:$id,filter:{search:$query},isOrder:$isOrder){
//       edges{
//         node{
//           imgUrl
//           sortOrder
//           id
//           imgUrl
//           insId
//           userName
//           published
//           instagram{
//             id
//           }
//         }
//       }
//       pageInfo {
//         ...PageInfoFragment
//       }
//     }
//   }
// `;
// export const useInstagramsByProductsId = makeQuery<
//   InstagramsByProductsId,
//   InstagramsByProductsIdVariables
// >(instagramsByProductsId);

const skuExtraChargeCategory = gql`
  query SkuExtraChargeCategory($variantId: ID!) {
    skuExtraChargeCategory(variantId: $variantId) {
      id
      name
      isActive
    }
  }
`;

export const useSkuExtraChargeCategoryQuery = makeQuery<
  SkuExtraChargeCategory,
  SkuExtraChargeCategoryVariables
>(skuExtraChargeCategory);

export const getErpProductVariant = gql`
  query GetErpProductVariant($sku: String!) {
    erpProductVariant(sku: $sku) {
      id
      attributes {
        name
        value
      }
      weight
      skuState
      alias
    }
  }
`;

export const useGetErpProductVariantQuery = makeQuery<
  GetErpProductVariant,
  GetErpProductVariantVariables
>(getErpProductVariant);

const bulkOperationProductsEvent = gql`
  query BulkOperationProductsEvent {
    bulkOperationProductsEvent {
      id
      startTime
      endTime
      message
      localFile
      operationUser
    }
  }
`;

export const useBulkOperationProductsEventQuery = makeQuery<
  BulkOperationProductsEvent,
  {}
>(bulkOperationProductsEvent);
